import { Clock } from "lucide-react";

import { useAppSelector } from "@/store/hooks";

import { formatEvmHumanBalance, normalizeCurrency } from "@/utils";

import { formatAmount } from "@/utils/formatAmount";

import type { WalletAsset } from "@/interfaces/asset";

interface Props {
  loading: boolean;
  quote: any;
  fromCurrency?: string;
  toCurrency?: string;
  amount: number;
  quoteExpiresIn: number;
  secondsLeft: number;
  toAsset: WalletAsset | undefined;

  /**
   * Parent controls the actual quote refresh.
   */
  refetchQuote: () => Promise<void>;
}

const SwapQuoteCard = ({
  loading,
  quote,
  fromCurrency,
  toCurrency,
  amount,
  quoteExpiresIn,
  secondsLeft,
  toAsset,
  refetchQuote,
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);

  if (loading) {
    return (
      <div
        className={`rounded-lg border p-4 ${
          theme === "dark"
            ? "border-border bg-muted"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        <div className="animate-pulse space-y-3">
          <div className="h-3 w-32 rounded bg-muted-foreground/20" />
          <div className="h-3 rounded bg-muted-foreground/20" />
          <div className="h-3 rounded bg-muted-foreground/20" />
          <div className="h-3 rounded bg-muted-foreground/20" />
        </div>
      </div>
    );
  }

  if (!quote) return null;

  return (
    <div
      className={`rounded-lg border p-4 ${
        theme === "dark"
          ? "border-border bg-muted"
          : "border-gray-200 bg-gray-50"
      }`}
    >
      {/* HEADER */}
      <div className="flex items-start justify-between border-b pb-3 mb-4">
        <div>
          <h3 className="font-semibold">Live Quote</h3>

          <p className="text-xs text-muted-foreground">
            Current market pricing
          </p>
        </div>

        <div className="flex items-center gap-2 text-right">
          <Clock className="h-4 w-4 text-muted-foreground" />

          <div>
            <div
              className={`text-sm font-medium ${
                secondsLeft <= 5 ? "text-red-500" : "text-emerald-500"
              }`}
            >
              Refresh {secondsLeft}s
            </div>

            <div
              className={`text-xs ${
                quoteExpiresIn <= 5 ? "text-red-500" : "text-muted-foreground"
              }`}
            >
              Expires {quoteExpiresIn}s
            </div>
          </div>
        </div>
      </div>

      {/* QUOTE DETAILS */}
      <div className="space-y-3 text-sm">
        {/* YOU PAY */}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">You Pay</span>

          <span className="font-medium">
            {formatAmount({
              amount,
              isFiat: quote?.settlement?.debitCurrency?.isFiat,
              decimals: quote?.settlement?.debitCurrency?.decimals,
            })}{" "}
            {normalizeCurrency(fromCurrency ?? "")}
          </span>
        </div>

        {/* FEE */}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Fee</span>

          <span className="font-medium text-amber-500">
            {formatAmount({
              amount: quote?.fee?.feeAmount,
              isFiat: quote?.fee?.feeCurrency?.isFiat,
              decimals: quote?.fee?.feeCurrency?.decimals,
            })}{" "}
            {quote?.fee?.feeCurrency?.symbol}
          </span>
        </div>

        {/* YOU RECEIVE */}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">You Receive</span>

          <span className="font-semibold text-emerald-500">
            {formatAmount({
              amount: quote?.conversion?.destinationAmount,
              isFiat: quote?.settlement?.debitCurrency?.isFiat,
              decimals: quote?.settlement?.debitCurrency?.decimals,
            })}{" "}
            {normalizeCurrency(toCurrency ?? "")}
          </span>
        </div>

        {/* RATE */}
        <div className="flex items-center justify-between border-t pt-3">
          <span className="text-muted-foreground">Rate</span>

          <span className="font-medium">
            1 {normalizeCurrency(fromCurrency ?? "")} ={" "}
            {formatEvmHumanBalance(
              quote?.exchangeRate?.rate ?? "0",
              Boolean(toAsset?.token?.isNative),
            )}{" "}
            {normalizeCurrency(toCurrency ?? "")}
          </span>
        </div>
      </div>

      {/* FOOTER */}
      <button
        onClick={refetchQuote}
        type="button"
        disabled={loading}
        className="mt-4 w-full rounded-lg border py-2 text-sm transition-colors hover:bg-primary/10 disabled:opacity-50"
      >
        Refresh Quote
      </button>
    </div>
  );
};

export default SwapQuoteCard;
